import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { GUIDES } from "@/data/guides";
import { motion } from "framer-motion";
import { BookOpen, Clock, ArrowRight } from "lucide-react";

const accentClasses = {
  blue: "bg-primary/10 text-primary",
  emerald: "bg-accent/15 text-emerald-700",
  yellow: "bg-secondary/20 text-yellow-700",
};

export default function Guides() {
  useEffect(() => {
    document.title = "Points Guides — Peak Points";
  }, []);

  return (
    <PublicLayout>
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-24 lg:px-8" data-testid="guides-page">
        <div className="max-w-2xl">
          <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-primary">
            Points Guides
          </p>
          <h1 className="mt-3 font-heading text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">
            Rewards, explained simply
          </h1>
          <p className="mt-4 font-body text-base leading-relaxed text-slate-600 md:text-lg">
            Short, no-fluff reads to help you understand what your points are
            worth and how to get the most from them.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {GUIDES.map((g, i) => (
            <motion.div
              key={g.slug}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
            >
              <Link
                to={`/guides/${g.slug}`}
                data-testid={`guide-card-${g.slug}`}
                className="group flex h-full flex-col rounded-3xl border border-border bg-card p-6 transition-transform duration-200 hover:-translate-y-1 hover:border-primary/40"
              >
                <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${accentClasses[g.accent] || accentClasses.blue}`}>
                  <BookOpen className="h-5 w-5" />
                </span>
                <div className="mt-4 flex items-center gap-3 font-heading text-xs font-bold uppercase tracking-wide text-slate-400">
                  <span>{g.category}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {g.readTime}</span>
                </div>
                <h2 className="mt-2 font-heading text-xl font-bold tracking-tight text-slate-900">
                  {g.title}
                </h2>
                <p className="mt-2 flex-1 font-body text-sm leading-relaxed text-slate-600">{g.dek}</p>
                <span className="mt-4 inline-flex items-center gap-1 font-heading text-sm font-bold text-primary">
                  Read guide <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </PublicLayout>
  );
}
